/**
 * Settings Layout Component
 * Navigation par onglets pour les paramètres
 *
 * Example component demonstrating i18n usage with useTranslation().
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  FileText,
  CreditCard,
  Crown,
  Network,
  Palette,
  Shield,
} from 'lucide-react';

interface SettingsTab {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

const settingsTabs: SettingsTab[] = [
  {
    id: 'company',
    labelKey: 'settings.tabs.company',
    icon: Building2,
    path: '/settings/company',
  },
  {
    id: 'legal',
    labelKey: 'settings.tabs.legal',
    icon: FileText,
    path: '/settings/legal',
  },
  {
    id: 'payments',
    labelKey: 'settings.tabs.payments',
    icon: CreditCard,
    path: '/settings/payments',
  },
  {
    id: 'integrations',
    labelKey: 'settings.tabs.integrations',
    icon: Network,
    path: '/settings/integrations',
  },
  {
    id: 'appearance',
    labelKey: 'settings.tabs.appearance',
    icon: Palette,
    path: '/settings/appearance',
  },
  {
    id: 'security',
    labelKey: 'settings.tabs.security',
    icon: Shield,
    path: '/settings/security',
  },
  {
    id: 'subscription',
    labelKey: 'settings.tabs.subscription',
    icon: Crown,
    path: '/settings/subscription',
  },
];

export const SettingsLayout: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Déterminer l'onglet actif
  const activeTab = settingsTabs.find(tab => location.pathname.startsWith(tab.path))?.id || 'company';

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-2xl">
          <Building2 className="w-8 h-8 text-teal-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
          <p className="text-gray-500 mt-0.5">{t('settings.subtitle')}</p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-1.5">
        <nav className="flex flex-wrap gap-1">
          {settingsTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-xl
                  font-medium text-sm transition-all duration-300
                  ${isActive
                    ? 'bg-white/70 text-teal-700 shadow-lg border border-white/60'
                    : 'text-gray-600 hover:bg-white/40 hover:text-gray-900'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{t(tab.labelKey)}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <Outlet />
    </div>
  );
};

export default SettingsLayout;
